import type { Container, Operation } from '@packages/core/container';

import { Ssm5Client } from '@packages/clients';

import type { Ssm5Diagram } from '../types';

import { toDiagramModules, toSupplyVoltage } from '../helpers/mappers';

/** Parameters of {@link GetDiagram}. */
export type GetDiagramParams = {
    /** The Cloud Connector ID of the open session. */
    cloudConnectorId: string;
    /** Aborts the read. */
    signal?: AbortSignal;
};

/**
 * Read the CAN Bus diagram of the vehicle in an open SSM5 session: the backend names the file of its
 * model and model year, reports every module on it with its DTCs and the vehicle's supply voltage, then
 * the file itself is read from where the backend put it. A model without a drawing keeps its modules.
 */
export class GetDiagram implements Operation {
    /**
     * Register this operation with the SSM5 client as its dependency.
     *
     * @param container - the container to register in
     */
    static registerInContainer(container: Container): void {
        container.register(GetDiagram, [Ssm5Client]);
    }

    /** @param client - the SSM5 API client */
    constructor(private client: Ssm5Client) {}

    /**
     * Read the diagram's modules, then its drawing when the backend names a file.
     *
     * @param params - {@link GetDiagramParams}
     * @returns the diagram, without a drawing when the vehicle has no file and without modules when the
     * backend listed none; rejects when aborted or when either read throws
     */
    async exec({ cloudConnectorId, signal }: GetDiagramParams): Promise<Ssm5Diagram> {
        const { diagram, ecus = [], vehicle } = await this.client.getDiagram({ cloudConnectorId }, signal);
        const modules = toDiagramModules(ecus);
        const supplyVoltage = toSupplyVoltage(vehicle?.battery_voltage);

        if (diagram.svgUrl === undefined || diagram.svgUrl === '') {
            return { modules, supplyVoltage };
        }

        const svg = await this.client.getDiagramSvg({ svgUrl: diagram.svgUrl }, signal);

        return { modules, supplyVoltage, svg };
    }
}
